import React, {useState} from 'react';
import {batch} from 'react-redux';
import {NoteType, OnStringSetState, QcJobStatuses, UpdateQcJobStatusParams} from '@pluto/@types';
import DeleteDialog from '@pluto/delete-dialog';
import {TimespanCategory} from '@pluto/rest';
import wrap from 'wraptile';
import {curry} from 'ramda';
import {RemoveQCReportMarkerAction, UpdateQCReportMarkerAction} from 'VideoAudioQC/Timeline/QCReport/Redux/QCReportActions';
import {QCNoteData} from 'VideoAudioQC/QCTabs/QCReport/List/List';
import {onQcNoteZoomIn} from 'VideoAudioQC/Timeline/TimelineUtils';
import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {OnEditMarkerProps} from '../../VideoAudioQCContainer';
import QCReport from './QCReport';
import {ToggleSelectedMarker} from '../../Timeline/Redux/TimelineActions';
import {
    TimelineRowType,
    QCReportMarker,
    TimelineMarker,
} from '../../Timeline/TimelineTypes';
import {OnEditQCReportMarkerProps} from './List/ListContainer';

import './QCReportContainer.scss';

type UpdateQcJobStatusType = (qcJobId: string, params: UpdateQcJobStatusParams) => void;

export interface QCReportContainerProps {
    item: Item;
    status: QcJobStatuses;
    identifier: string;
    markers: QCReportMarker[];
    selectedRowId: string;
    selectedMarkerId: string;
    noteTypes: TimespanCategory[];
    onGoToMarker: (frame: number) => Promise<void>;
    updateQcJobReportStatus: UpdateQcJobStatusType;
    onCreateMarker: (rowId: string) => void;
    onEditMarkerIn: (props: OnEditMarkerProps) => void;
    onEditMarkerOut: (props: OnEditMarkerProps) => void;
    toggleSelectedMarker: ToggleSelectedMarker;
    updateQCReportMarker: UpdateQCReportMarkerAction;
    removeQCReportMarker: RemoveQCReportMarkerAction;
    jobDeliveryDate: string;
}

export const onShowDeleteDialog = curry((setDeleteId: OnStringSetState, item: QCReportMarker) => {
    const {identifier} = item;
    
    setDeleteId(identifier);
});

export type DeleteDialogProps = {
    item: Item
    deleteId: string,
    setDeleteId: OnStringSetState,
    removeQCReportMarker: RemoveQCReportMarkerAction,
};

export const onCloseDialog = curry((props: DeleteDialogProps, confirm: boolean) => {
    const {
        item,
        deleteId,
        setDeleteId,
        removeQCReportMarker,
    } = props;
    
    if (confirm) {
        removeQCReportMarker(item.notes, deleteId);
    }
    
    setDeleteId('');
});

export const titleText = 'Are you sure you want to delete the note?';

export const getDeleteDialog = (props: DeleteDialogProps) => {
    const {deleteId} = props;
    const handleClose = onCloseDialog(props);
    
    if (deleteId === '')
        return null;
    
    return (
        <DeleteDialog
            text={titleText}
            onCloseDialog={handleClose}
        />
    );
};

export const onEditMarker = curry((item: Item, updateQCReportMarker: UpdateQCReportMarkerAction, markerProps: OnEditQCReportMarkerProps) => {
    const {duration} = item.video;
    const {
        marker,
        inpoint,
    } = markerProps;
    
    updateQCReportMarker({
        url: item.notes,
        duration,
        marker,
        inpoint,
    });
});

export const seekAndSelect = async (props: QCReportContainerProps, data: QCNoteData, toStart: boolean) => {
    const {
        identifier,
        selectedMarkerId,
        onGoToMarker,
        toggleSelectedMarker,
    } = props;
    const {
        start,
        end,
    } = data;
    
    if (start === undefined || end === undefined)
        return;
    
    const time = toStart ? start : end;
    
    await onGoToMarker(time);
    
    if (selectedMarkerId !== data.identifier)
        toggleSelectedMarker(data.identifier, identifier, TimelineRowType.QCReport);
};

export const onGoToMarkerClick = curry((props: QCReportContainerProps, toStart: boolean, data: QCNoteData) => {
    seekAndSelect(props, data, toStart);
});

export type OnEditMarkerInOutClickProps = {
    identifier: string;
    selectedMarkerId: string;
    onEditMarkerInOut: (props: OnEditMarkerProps) => void;
    toggleSelectedMarker: ToggleSelectedMarker;
};

export const dispatchMarkerInOutClick = wrap((props: OnEditMarkerInOutClickProps, selectedMarker: TimelineMarker) => {
    const {
        identifier,
        selectedMarkerId,
        onEditMarkerInOut,
        toggleSelectedMarker,
    } = props;
    
    if (selectedMarkerId !== selectedMarker.identifier) {
        toggleSelectedMarker(
            selectedMarker.identifier,
            identifier,
            TimelineRowType.QCReport,
        );
    }
    
    onEditMarkerInOut({
        selectedMarker,
        selectedType: TimelineRowType.QCReport,
    });
});

export const onEditMarkerInOutClick = curry((props: OnEditMarkerInOutClickProps, data: QCNoteData) => {
    const {start, end} = data;
    
    if (start === undefined || end === undefined)
        return;
    
    const selectedMarker = data as unknown as TimelineMarker;
    
    batch(dispatchMarkerInOutClick(props, selectedMarker));
});

export const onClickOutsideList = wrap((props: QCReportContainerProps) => {
    const {
        identifier,
        toggleSelectedMarker,
        selectedMarkerId,
    } = props;
    
    if (!selectedMarkerId)
        return;
    
    // Deselect selected marker
    toggleSelectedMarker(selectedMarkerId, identifier, TimelineRowType.QCReport);
});

export default function QCReportContainer(props: QCReportContainerProps) {
    const {
        status,
        item,
        identifier,
        markers,
        selectedMarkerId,
        noteTypes,
        onEditMarkerIn,
        onEditMarkerOut,
        updateQCReportMarker,
        removeQCReportMarker,
        toggleSelectedMarker,
        jobDeliveryDate,
    } = props;
    const [deleteId, setDeleteId] = useState<string>('');
    const handlePassed = onHandleStatusChanged(props, QcJobStatuses.Passed);
    const handleFailed = onHandleStatusChanged(props, QcJobStatuses.Failed);
    const handleResetStatus = onHandleResetStatus(props);
    const handleMarkerClicked = onMarkerClick(props, onQcNoteZoomIn);
    const handleCreateMarkerClick = onCreateMarkerClick(props);
    const handleShowDeleteDialog = onShowDeleteDialog(setDeleteId);
    const handleEditMarker = onEditMarker(item, updateQCReportMarker);
    const handleGoToMarkerStartClick = onGoToMarkerClick(props, true);
    const handleGoToMarkerEndClick = onGoToMarkerClick(props, false);
    const handleEditMarkerIn = onEditMarkerInOutClick({
        identifier,
        selectedMarkerId,
        toggleSelectedMarker,
        onEditMarkerInOut: onEditMarkerIn,
    });
    const handleEditMarkerOut = onEditMarkerInOutClick({
        identifier,
        selectedMarkerId,
        toggleSelectedMarker,
        onEditMarkerInOut: onEditMarkerOut,
    });
    const handleOnClickOutsideList = onClickOutsideList(props);
    
    const deleteDialog = getDeleteDialog({
        deleteId,
        item,
        setDeleteId,
        removeQCReportMarker,
    });
    
    return (
        <div className="qc-report-tab-content-container">
            <QCReport
                jobDeliveryDate={jobDeliveryDate}
                status={status}
                markers={markers}
                selectedMarkerId={selectedMarkerId}
                item={item}
                noteTypes={noteTypes}
                onCreateMarker={handleCreateMarkerClick}
                onMarkerClick={handleMarkerClicked}
                handlePassed={handlePassed}
                handleFailed={handleFailed}
                resetStatus={handleResetStatus}
                onDeleteMarker={handleShowDeleteDialog}
                onEditMarker={handleEditMarker}
                onEditMarkerIn={handleEditMarkerIn}
                onEditMarkerOut={handleEditMarkerOut}
                onGoToMarkerStartClick={handleGoToMarkerStartClick}
                onGoToMarkerEndClick={handleGoToMarkerEndClick}
                onClickOutsideList={handleOnClickOutsideList}
            />
            {deleteDialog}
        </div>
    );
}

export const onMarkerClick = curry((props: QCReportContainerProps, onQcNoteZoomIn: (marker: QCNoteData) => void, marker: QCNoteData) => {
    const {
        identifier,
        toggleSelectedMarker,
    } = props;
    
    // always select marker
    toggleSelectedMarker(marker.identifier, identifier, TimelineRowType.QCReport);
    
    onQcNoteZoomIn(marker);
});

export const onCreateMarkerClick = wrap((props: QCReportContainerProps) => {
    const {
        identifier,
        onCreateMarker,
    } = props;
    
    onCreateMarker(identifier);
});

export const onHandleStatusChanged = wrap((props: QCReportContainerProps, status: QcJobStatuses) => {
    const {
        item,
        updateQcJobReportStatus,
    } = props;
    const {id} = item;
    
    updateQcJobReportStatus(String(id), {
        type: NoteType.media,
        status,
    });
});

export const onHandleResetStatus = wrap((props: QCReportContainerProps) => {
    const {
        item,
        updateQcJobReportStatus,
    } = props;
    const {id} = item;
    
    updateQcJobReportStatus(String(id), {
        type: NoteType.media,
        status: QcJobStatuses.Pending,
    });
});
